import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import * as Step from "./Steps";
import { calculateTotalRepaymentDifference, calculateTotalRepaymentWithLowerInterest, isKOResult } from "./domain";
import type { FormFields, MortgageHomeStartCalcRequest, MortgageHomeStartCalcResponse } from "./types";

interface State<TFormFields extends FormFields = FormFields> extends AbstractLeadFormState<TFormFields> {
	leadformResponse: any;
	koMessage?: ReactNode;
	isKO?: boolean;
	redirectionUrl?: string;
}

/**
 * @Shortcode
 *
 * @title "Otthon Start"
 * @shortcode "home-start-small-calculator"
 * @slug "otthon-start"
 * @type "calculator"
 */
export class MortgageHomeStartSmallCalculator extends AbstractLeadForm<
	MortgageHomeStartCalcRequest,
	MortgageHomeStartCalcResponse,
	FormFields,
	AbstractLeadFormProps<MortgageHomeStartCalcRequest, MortgageHomeStartCalcResponse>,
	State
> {
	// EventParams
	protected readonly productTypes: string = "MORTGAGE-OTTHON_START";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérj ingyenes segítséget szakértőnktől!",
		outsideTitle: "Otthon Start kalkulátor",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			leadformResponse: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values: { email, loanAmount },
			submitState,
			koMessage,
			redirectionUrl,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result" />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					canStepBack
					calculatedDifference={calculateTotalRepaymentDifference(loanAmount)}
				/>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					button="Elküldöm"
					submitState={submitState}
					colorVersion="black"
					newsletterOption
					newsletterText="Szeretnék emailt kapni az újdonságokról, ajánlatokról, kedvezményekről."
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
				<Step.KoPage stepName="ko-page" koMessage={koMessage} redirectionUrl={redirectionUrl} canStepBack />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const {
			values: { wantsFirstHouse, loanAmount, monthlyIncome, has2YearsLegalHealthInsurance },
		} = this.state;

		switch (target) {
			case "success-page":
				this.onSuccessPage();
				break;

			case "result": {
				const { isKO, koMessage, redirectionUrl } = isKOResult(
					loanAmount,
					monthlyIncome,
					wantsFirstHouse,
					has2YearsLegalHealthInsurance,
				);
				this.setState({
					isKO,
					koMessage,
					redirectionUrl,
				});

				this.sendEvent("BM_result");

				if (isKO) {
					super.onStep("ko-page", withEvent);
				} else {
					super.onStep("result", withEvent);
				}
				break;
			}

			default:
				super.onStep(target, withEvent);
		}
	}

	private onSuccessPage(): void {
		this.setState({
			submitState: "loading",
		});
		this.sendLead()
			.then((_response) => {
				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				logError(error);
			});
	}

	protected getCalculatorRequest(): MortgageHomeStartCalcRequest {
		const {
			values: { loanAmount, realEstateValue, monthlyIncome },
		} = this.state;

		return {
			...super.getCalculatorRequest(),

			realEstateValue,
			loanAmount,
			maturity: 25,
			incomeList: [
				{
					incomeType: "SALARY-H",
					incomeTypeName: "Magyar alkalmazotti jövedelem",
					amount: monthlyIncome,
					receiptAmount: monthlyIncome,
				},
			],
		};
	}

	protected getLeadDataRequest(): MortgageHomeStartCalcRequest {
		const {
			values: {
				loanAmount,
				monthlyIncome,
				realEstateValue,
				has2YearsLegalHealthInsurance,
				wantsFirstHouse,
				typeOfProperty,
			},
		} = this.state;

		const typeOfPropertyString = typeOfProperty ? "lakás" : "önálló lakóház";
		const request = this.getCalculatorRequest();
		const otherData = new JsonDataBuilder(request.otherDataJson);

		const fieldMappings = [
			["Hitelösszeg", "loanAmount", loanAmount, "ft"],
			["Ingatlan értéke", "realEstateValue", realEstateValue, "ft"],
			["Havi jövedelem", "monthlyIncome", monthlyIncome, "ft"],
			[
				"Van legalább 2 éves TB jogviszony",
				"has2YearsLegalHealthInsurance",
				has2YearsLegalHealthInsurance,
				"bool",
			],
			["Első ingatlant vásárolnak?", "wantsFirstHouse", wantsFirstHouse, "bool"],
			["Megvásárolandó ingatlan típusa", "typeOfProperty", typeOfPropertyString, "string"],
		] as const;

		fieldMappings.forEach(([label, key, value, type]) => {
			otherData.addItem(label, key, value, type);
		});

		return {
			...request,
			otherDataJson: otherData.getJson(),
		};
	}
	public getLeadData(): LeadRequest<MortgageHomeStartCalcRequest, MortgageHomeStartCalcResponse> {
		const data = super.getLeadData();

		data.productSubType = "OTTHON_START";
		data.administrationLocation = data.cityId;
		data.cityId = undefined;

		return data;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-otthon-start";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const { loanAmount } = this.state.values;
		const data = this.getLeadData();
		const { monthlyInstallment, totalRepayment } = calculateTotalRepaymentWithLowerInterest(loanAmount);

		return {
			lastName: data.lastName,
			firstName: data.firstName,
			loanAmount: formatText(Math.round(loanAmount), "ft"),
			monthlyInstallment: formatText(Math.round(monthlyInstallment), "ft"),
			totalRepayment: formatText(Math.round(totalRepayment), "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "MORTGAGE";
	}

	protected getComponentName(): string {
		return "MortgageHomeStartSmallCalculator";
	}
}

export default MortgageHomeStartSmallCalculator;
